import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTenantContext } from 'src/context/useTenantContext';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useCancelInvitation, useInviteMember } from 'src/hooks/useInvitations';
import { useMemberships } from 'src/hooks/useOrganizations';
import { tenantEmployeeClearInvited, tenantEmployeeMarkInvited } from 'src/services/apis/tenantRequests';
import { QUERIES } from 'src/utils/react-query';

export type InviteInput = { employeeId: string; email: string; role: 'member' | 'admin' };

type Ports = {
  inviteMember: (input: { email: string; role: 'member' | 'admin' }) => Promise<unknown>;
  markInvited: (employeeId: string) => Promise<unknown>;
};

/**
 * Pure orchestration, exported for its own test: invite through better-auth FIRST, then
 * record it on the server. A failed stamp costs a re-click (the row still reads "No
 * account", Invite is clickable again — a resend, harmless); a failed invite after a stamp
 * would show "Invited" for someone who was never emailed, and nothing would ever correct it.
 */
export async function inviteEmployee(input: InviteInput, ports: Ports): Promise<void> {
  await ports.inviteMember({ email: input.email, role: input.role });
  await ports.markInvited(input.employeeId);
}

/**
 * `useTenantContext` (src/context/useTenantContext) exposes orgSlug/restaurantId/branchId
 * only — no organizationId. better-auth's organization id is resolved the way
 * OrgSettings/Invitations.tsx and OrgSettings/Members.tsx do: match the active session's
 * organization against the membership list, falling back to the first membership.
 */
export function useInviteEmployee() {
  const { orgSlug, restaurantId } = useTenantContext();
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const active = useMemo(
    () => memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0],
    [memberships, activeOrganizationId]
  );
  const organizationId = active?.id ?? null;

  const queryClient = useQueryClient();
  const inviteMember = useInviteMember(organizationId);
  const cancelInvitation = useCancelInvitation(organizationId);
  const scope = { orgSlug, restaurantId };

  const invite = useMutation({
    mutationFn: (input: InviteInput) =>
      inviteEmployee(input, {
        inviteMember: (payload) => inviteMember.mutateAsync(payload),
        markInvited: (employeeId) => tenantEmployeeMarkInvited(scope, employeeId),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.EMPLOYEES] }),
  });

  const cancel = useMutation({
    mutationFn: async (input: { employeeId: string; invitationId: string }) => {
      await cancelInvitation.mutateAsync(input.invitationId);
      await tenantEmployeeClearInvited(scope, input.employeeId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERIES.EMPLOYEES] }),
  });

  return { invite, cancel, organizationId };
}
